/**
 * Realtor ListingCard.
 *
 * Extracted from the listings-list "Cards" view so it can be reused
 * verbatim by the dashboard hot-listings grid (B3). Self-contained: it
 * derives its own status badge and formats its own price, so any caller
 * that has a `Listing` can render a consistent card.
 */

import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import type {
	Listing,
	ListingStatus,
} from "@/features/realtor/services/listings-api.service";
import {
	deriveListingStatusBucket,
	type ListingStatusBucket,
	listingStatusBucketI18nKey,
} from "@/features/realtor/utils/listing-status";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";

const BUCKET_BADGE_CLASS: Record<ListingStatusBucket, string> = {
	live: "bg-dashboard-pill-active-bg text-dashboard-pill-active",
	draft: "border-dashboard-border bg-dashboard-bg text-dashboard-muted",
	under_negotiation:
		"bg-dashboard-pill-awaiting-bg text-dashboard-pill-awaiting",
	sold: "bg-dashboard-pill-done-bg text-dashboard-pill-done",
	withdrawn: "bg-dashboard-pill-neutral-bg text-dashboard-pill-neutral",
};

/**
 * Derived status badge shared by the cards, the table rows, and (later)
 * the dashboard. Takes `status` + `isPublic` and buckets them the same
 * way `deriveListingStatusBucket` does.
 */
export function ListingStatusBadge({
	status,
	isPublic,
}: {
	status: ListingStatus;
	isPublic: boolean;
}) {
	const { t } = useTranslation();
	const bucket = deriveListingStatusBucket({ status, isPublic });
	return (
		<span
			className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium shrink-0 border ${BUCKET_BADGE_CLASS[bucket]}`}
		>
			{t(listingStatusBucketI18nKey(bucket))}
		</span>
	);
}

export interface ListingCardProps {
	listing: Listing;
	onClick: () => void;
}

export function ListingCard({ listing, onClick }: ListingCardProps) {
	const { t } = useTranslation();
	return (
		<Card
			className="cursor-pointer transition border-dashboard-border bg-dashboard-bg hover:bg-dashboard-surface focus-within:ring-2 focus-within:ring-dashboard-accent"
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick();
				}
			}}
			role="button"
			tabIndex={0}
		>
			<CardContent className="p-4 space-y-2">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-dashboard-text truncate">
							{listing.headline}
						</p>
						<p className="text-xs text-dashboard-muted truncate">
							{listing.locality} · {listing.city}
						</p>
					</div>
					<ListingStatusBadge
						status={listing.status}
						isPublic={listing.isPublic}
					/>
				</div>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-dashboard-muted">
					<span
						style={{ fontFamily: "var(--font-display)", fontWeight: 500 }}
						className="text-dashboard-text"
					>
						{formatPaiseAsRupees(listing.askingPrice)}
					</span>
					{listing.bhk != null && <span>· {listing.bhk} BHK</span>}
					{listing.sizeSqft != null && (
						<span>
							· {listing.sizeSqft} {t("realtor.listings.sqft")}
						</span>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
